import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import { useAppSelector } from 'hooks/reduxHooks'
import noop from 'lib/function/noop'
import type { PropsWithChildren } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { canReplayAgentHubBookingOnboarding } from './canStartAgentHubBookingOnboarding'
import useAgentHubBookingOnboardingAutoStart from './useAgentHubBookingOnboardingAutoStart'
import type { AgentHubBookingOnboardingStatus } from './agentHubBookingOnboardingStorage'
import {
  getAgentHubBookingOnboardingStatus,
  setAgentHubBookingOnboardingStatus,
  subscribeAgentHubBookingOnboardingStatus,
} from './agentHubBookingOnboardingStorage'

type AgentHubBookingOnboardingContextValue = {
  complete: () => void
  dismiss: () => void
  isActive: boolean
  start: () => void
  status?: AgentHubBookingOnboardingStatus
}

const AgentHubBookingOnboardingContext = createContext<AgentHubBookingOnboardingContextValue>({
  complete: noop,
  dismiss: noop,
  isActive: false,
  start: noop,
})

export function useAgentHubBookingOnboardingStatus(): AgentHubBookingOnboardingStatus | undefined {
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const [status, setStatus] = useState<AgentHubBookingOnboardingStatus | undefined>(() => (
    memberId ? getAgentHubBookingOnboardingStatus(memberId) : undefined
  ))

  useEffect(() => {
    function readStatus() {
      setStatus(memberId ? getAgentHubBookingOnboardingStatus(memberId) : undefined)
    }

    readStatus()

    return subscribeAgentHubBookingOnboardingStatus(readStatus)
  }, [memberId])

  return status
}

export function AgentHubBookingOnboardingProvider({ children }: PropsWithChildren) {
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const status = useAgentHubBookingOnboardingStatus()
  const [isActive, setIsActive] = useState(false)

  useEffect(() => {
    setIsActive(false)
  }, [memberId])

  const start = useCallback(() => {
    if (!canReplayAgentHubBookingOnboarding({
      isAgentHubEnabled,
      isLoggedIn,
      isSpoofed,
    })) {
      return
    }

    setIsActive(true)
  }, [isLoggedIn, isSpoofed])

  const dismiss = useCallback(() => {
    if (memberId) {
      setAgentHubBookingOnboardingStatus(memberId, 'dismissed')
    }
    setIsActive(false)
  }, [memberId])

  const complete = useCallback(() => {
    if (memberId) {
      setAgentHubBookingOnboardingStatus(memberId, 'completed')
    }
    setIsActive(false)
  }, [memberId])

  useAgentHubBookingOnboardingAutoStart({
    isActive,
    start,
    status,
  })

  const value = useMemo<AgentHubBookingOnboardingContextValue>(() => ({
    complete,
    dismiss,
    isActive,
    start,
    status,
  }), [complete, dismiss, isActive, start, status])

  return (
    <AgentHubBookingOnboardingContext.Provider value={value}>
      {children}
    </AgentHubBookingOnboardingContext.Provider>
  )
}

function useAgentHubBookingOnboarding(): AgentHubBookingOnboardingContextValue {
  return useContext(AgentHubBookingOnboardingContext)
}

export default useAgentHubBookingOnboarding
